/**
 * Self Improvement Loop — the review workflow.
 *
 *   Creative Plan → Art Director → Revision Engine → Art Director → … → Approved
 *
 * The loop improves the Creative Plan and nothing else. It never generates
 * design, never produces Design JSON, and cannot run forever: it stops on
 * approval, on the iteration ceiling, when confidence stops improving, or when
 * no meaningful revision remains — and every path out is one of those four.
 *
 * Both the reviewer and the Revision Engine are injectable, so a future OpenAI
 * engine slots in without changing this file or the public API.
 */

import { reviewCreativePlan } from '../artDirector';
import { LOOP_SCHEMA_ID, REVISION_VERSION, STOP_REASONS, DEFAULT_LOOP_OPTIONS } from './revisionSchema';
import { defaultRevisionEngine, isRevisionEngine } from './revisionEngine';

const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Run the improvement loop over a Creative Plan.
 *
 * @param {Object} inputs
 * @param {Object} inputs.plan          the Creative Plan (from the Thinking Engine).
 * @param {Object} inputs.research      Research Brief.
 * @param {Object} inputs.genome        Style Genome.
 * @param {Object[]} inputs.patterns    selected Library Patterns.
 * @param {Object} [inputs.constraints] memory constraints.
 * @param {Object} [options]
 * @param {number} [options.maxIterations]
 * @param {number} [options.minConfidenceGain]
 * @param {Object} [options.engine]     a RevisionEngine (future: OpenAI).
 * @param {Function} [options.reviewer] a reviewer (defaults to the Art Director).
 * @param {Object} [options.thresholds] passed to the reviewer.
 * @returns {Promise<Object>} { schema, version, approved, plan, patterns, review, iterations, stopReason, confidenceDelta, history }
 */
export const runImprovementLoop = async (inputs = {}, options = {}) => {
  const { maxIterations, minConfidenceGain } = { ...DEFAULT_LOOP_OPTIONS, ...asObject(options) };
  const engine = isRevisionEngine(options.engine) ? options.engine : defaultRevisionEngine;
  const reviewer = typeof options.reviewer === 'function' ? options.reviewer : reviewCreativePlan;

  const base = asObject(inputs);
  const review = (plan, patterns) => reviewer(
    { plan, patterns, research: base.research, genome: base.genome, constraints: base.constraints },
    { thresholds: options.thresholds },
  );

  let plan = asObject(base.plan);
  let patterns = Array.isArray(base.patterns) ? base.patterns : [];
  let current = review(plan, patterns);

  const initialConfidence = current.overallConfidence;
  // Never hand back work worse than what we were given.
  let best = { plan, patterns, review: current };
  const history = [{ iteration: 0, approved: current.approved, overallConfidence: current.overallConfidence, scores: current.scores, weaknesses: current.weaknesses.map((w) => w.rule) }];

  let stopReason = STOP_REASONS.APPROVED;
  let iterations = 0;

  while (!current.approved) {
    if (iterations >= maxIterations) { stopReason = STOP_REASONS.MAX_ITERATIONS; break; }
    iterations += 1;

    const revision = asObject(await engine.revise({
      plan, review: current, research: base.research, genome: base.genome, patterns, constraints: base.constraints,
    }));
    const changed = Array.isArray(revision.changedDecisions) ? revision.changedDecisions : [];

    if (changed.length === 0) {
      // The outstanding notes need re-thinking, not revision. Send it back.
      iterations -= 1;
      stopReason = STOP_REASONS.NO_MEANINGFUL_REVISION;
      history.push({ iteration: iterations + 1, revisionSummary: revision.revisionSummary, changedDecisions: [], unaddressed: revision.unaddressed || [] });
      break;
    }

    const nextPlan = asObject(revision.plan);
    const nextPatterns = Array.isArray(revision.patterns) ? revision.patterns : patterns;
    const nextReview = review(nextPlan, nextPatterns);

    history.push({
      iteration: iterations,
      approved: nextReview.approved,
      overallConfidence: nextReview.overallConfidence,
      scores: nextReview.scores,
      weaknesses: nextReview.weaknesses.map((w) => w.rule),
      revisionSummary: revision.revisionSummary,
      changedDecisions: changed.map((c) => c.key),
      reasoning: revision.reasoning || [],
      confidenceDelta: revision.confidenceDelta,
      unaddressed: revision.unaddressed || [],
    });

    const gain = nextReview.overallConfidence - current.overallConfidence;
    if (nextReview.overallConfidence > best.review.overallConfidence) best = { plan: nextPlan, patterns: nextPatterns, review: nextReview };

    plan = nextPlan;
    patterns = nextPatterns;
    current = nextReview;

    if (current.approved) { stopReason = STOP_REASONS.APPROVED; break; }
    // Revision is no longer buying anything — further rounds would only churn.
    if (gain < minConfidenceGain) { stopReason = STOP_REASONS.NO_CONFIDENCE_GAIN; break; }
  }

  if (current.approved) { stopReason = STOP_REASONS.APPROVED; best = { plan, patterns, review: current }; }

  return {
    schema: LOOP_SCHEMA_ID,
    version: REVISION_VERSION,
    approved: best.review.approved,
    plan: best.plan,
    patterns: best.patterns,
    review: best.review,
    iterations,
    stopReason,
    confidenceDelta: round2(best.review.overallConfidence - initialConfidence),
    history,
  };
};
